import React from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import {
  mapMergeUseQueriesResults,
  stableQueryKey,
  useMemoizedValue,
} from '@neptune/shared/common-util';
import { EntitiesLists, Entity, listEntities } from '@neptune/shared/entity-domain';

import {
  KnownAttributes,
  knownAttributesForExperimentIdentification,
} from 'domain/experiment/attribute';

const EMPTY_SELECTION: Entity[] = [];

const QUERY_KEY = 'entity-identification-map';

export function useEntityIdentificationEntries(
  experimentNamesByProject: Map<string, string[]>,
  shortIdsByProject: Map<string, string[]>,
) {
  const queryClient = useQueryClient();

  const results = useQueries({
    queries: React.useMemo(
      () => createQueries(experimentNamesByProject, shortIdsByProject),
      [experimentNamesByProject, shortIdsByProject],
    ),
  });

  const invalidateQuery = React.useCallback(
    (projectId?: string) => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: makeQueryKey(projectId) });
      } else {
        const projectIdentifiers = new Set([
          ...experimentNamesByProject.keys(),
          ...shortIdsByProject.keys(),
        ]);

        projectIdentifiers.forEach((projectIdentifier) => {
          queryClient.invalidateQueries({ queryKey: makeQueryKey(projectIdentifier) });
        });
      }
    },
    [experimentNamesByProject, shortIdsByProject, queryClient],
  );

  // useQueries return value is not stable (only the [*].data fields are),
  // so useMemo would be pointless here, unless used with dynamic deps array.
  const result = useMemoizedValue(mapMergeUseQueriesResults(results));

  return { result, invalidateQuery, isFetching: results.some((result) => result.isFetching) };
}

function createQueries(
  experimentNamesByProject: Map<string, string[]>,
  shortIdsByProject: Map<string, string[]>,
) {
  const projectIdentifiers = new Set([
    ...experimentNamesByProject.keys(),
    ...shortIdsByProject.keys(),
  ]);

  return Array.from(projectIdentifiers).flatMap((projectIdentifier) => {
    const experimentNames = experimentNamesByProject.get(projectIdentifier);
    const shortIds = shortIdsByProject.get(projectIdentifier);

    return [
      {
        cacheTime: 1_000,
        staleTime: 1_000,
        keepPreviousData: true,
        queryFn: async (): Promise<[string, EntitiesLists]> => [
          projectIdentifier,
          await queryFields(projectIdentifier, experimentNames, shortIds),
        ],
        queryKey: makeQueryKey(projectIdentifier, experimentNames, shortIds),
      },
    ];
  });
}

function makeQueryKey(projectIdentifier: string, experimentNames?: string[], shortIds?: string[]) {
  return stableQueryKey(
    QUERY_KEY,
    projectIdentifier,
    ...(experimentNames ? [{ experimentNames }] : []),
    ...(shortIds ? [{ shortIds }] : []),
  );
}

const FIELDS = [
  ...knownAttributesForExperimentIdentification,
  KnownAttributes.GroupTags,
  KnownAttributes.CustomRunId,
];

async function queryFields(
  projectIdentifier: string,
  experimentNames?: string[],
  shortIds?: string[],
) {
  if (!experimentNames?.length && !shortIds?.length) {
    return Promise.resolve({ experiments: EMPTY_SELECTION, runs: EMPTY_SELECTION });
  }

  return listEntities(
    {
      projectIdentifier,
      experimentNames,
      runsIds: shortIds,
      attributePathsFilter: FIELDS,
    },
    {
      filterArchived: false,
    },
  );
}
